import React, { useState, useRef } from "react";
import {
  Share2,
  Copy,
  Download,
  Link,
  Check,
  Image as ImageIcon,
} from "lucide-react";

const MarketingTools = ({ userData = { referralCode: "ABC123" } }) => {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("enlaces");
  const [activeCopyId, setActiveCopyId] = useState(null);
  const textAreaRef = useRef(null);

  // URL base para los enlaces de referido
  const baseUrl =
    process.env.REACT_APP_REFERRAL_BASE_URL ||
    "https://deportivospro.com/prediccion-zone/join?ref=";
  const referralLink = `${baseUrl}${userData?.referralCode || ""}`;

  // Función para copiar el enlace o código al portapapeles
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setActiveCopyId(id);
    setTimeout(() => setActiveCopyId(null), 2000);
  };

  // Función para compartir en redes sociales
  const shareOnSocial = (platform) => {
    let shareUrl;
    const message = `¡Únete a mí en esta increíble plataforma! Regístrate con mi código de referido: ${userData?.referralCode}`;

    switch (platform) {
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${encodeURIComponent(
          message + " " + referralLink
        )}`;
        break;
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          referralLink
        )}&quote=${encodeURIComponent(message)}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          message
        )}&url=${encodeURIComponent(referralLink)}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, "_blank");
  };

  // Función para descargar imagen
  const downloadImage = (imageUrl, filename) => {
    // Create a temporary link element to trigger download
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Contenido dummy para las imágenes de marketing
  const marketingImages = [
    {
      id: 1,
      type: "banner",
      title: "Banner Principal",
      placeholder:
        "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSC7VPGXHdljXURCf48y-baZgdr62925iMtEw&s",
      filename: "../img/Banner1.png",
    },
    {
      id: 2,
      type: "story",
      title: "Story Instagram",
      placeholder: "../img/Banner1.png",
      filename: "story-referido.jpg",
    },
    {
      id: 3,
      type: "post",
      title: "Post para Redes",
      placeholder: "/api/placeholder/500/500",
      filename: "post-referido.jpg",
    },
    {
      id: 4,
      type: "banner-small",
      title: "Banner Pequeño",
      placeholder: "/api/placeholder/450/220",
      filename: "banner-small-referido.jpg",
    },
  ];

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 shadow-xl border border-gray-700">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-white flex items-center">
          <Share2 className="mr-3 text-blue-400" size={24} />
          Herramientas de Marketing
        </h3>
      </div>

      {/* Tabs de navegación mejorados */}
      <div className="flex border-b border-gray-700 mb-6">
        {["enlaces", "multimedia", "compartir"].map((tab) => (
          <button
            key={tab}
            className={`px-6 py-3 font-medium text-sm transition-all duration-200 ${
              activeTab === tab
                ? "text-blue-400 border-b-2 border-blue-400 font-semibold"
                : "text-gray-400 hover:text-gray-300 hover:bg-gray-800/50"
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Contenido para Enlaces */}
      {activeTab === "enlaces" && (
        <div className="space-y-6 animate-fadeIn">
          <div className="bg-gray-800/70 p-5 rounded-lg border border-gray-700 hover:border-gray-600 transition-all shadow-md">
            <div className="flex justify-between items-center mb-3">
              <label className="text-gray-300 font-medium flex items-center">
                <Link size={16} className="text-blue-400 mr-2" />
                Enlace de invitación
              </label>
              <button
                onClick={() => copyToClipboard(referralLink, "link")}
                className={`flex items-center text-sm px-3 py-1.5 rounded transition-all ${
                  activeCopyId === "link"
                    ? "bg-green-600 text-white"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                {activeCopyId === "link" ? (
                  <Check size={16} className="mr-1" />
                ) : (
                  <Copy size={16} className="mr-1" />
                )}
                {activeCopyId === "link" ? "¡Copiado!" : "Copiar"}
              </button>
            </div>
            <div className="flex bg-gray-900 p-3 rounded-md text-gray-200 text-sm break-all border border-gray-700">
              <span>{referralLink}</span>
            </div>
          </div>

          <div className="bg-gray-800/70 p-5 rounded-lg border border-gray-700 hover:border-gray-600 transition-all shadow-md">
            <div className="flex justify-between items-center mb-3">
              <label className="text-gray-300 font-medium">
                Código de referido
              </label>
              <button
                onClick={() =>
                  copyToClipboard(userData?.referralCode || "", "code")
                }
                className={`flex items-center text-sm px-3 py-1.5 rounded transition-all ${
                  activeCopyId === "code"
                    ? "bg-green-600 text-white"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                {activeCopyId === "code" ? (
                  <Check size={16} className="mr-1" />
                ) : (
                  <Copy size={16} className="mr-1" />
                )}
                {activeCopyId === "code" ? "¡Copiado!" : "Copiar"}
              </button>
            </div>
            <div className="bg-gray-900 py-4 px-3 rounded-md border border-gray-700 flex items-center justify-center">
              <div className="text-center">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                  Tu código único
                </div>
                <code className="text-2xl font-bold text-blue-400 tracking-widest">
                  {userData?.referralCode || "----"}
                </code>
              </div>
            </div>
          </div>

          <div className="bg-blue-600/20 p-4 rounded-lg border border-blue-600/30">
            <div className="flex items-start">
              <div className="bg-blue-500/30 p-2 rounded-full mr-3">
                <ImageIcon size={18} className="text-blue-300" />
              </div>
              <div>
                <h4 className="text-blue-300 font-medium mb-1">Consejo Pro</h4>
                <p className="text-gray-300 text-sm">
                  Comparte tu código en grupos de interés relacionados con
                  deportes y apuestas para maximizar tus referidos.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contenido para Multimedia */}
      {activeTab === "multimedia" && (
        <div className="space-y-5 animate-fadeIn">
          <div className="flex items-center justify-between">
            <p className="text-gray-300">
              Descarga estos materiales para promocionar en tus redes sociales:
            </p>
            <span className="bg-blue-900/40 text-blue-300 text-xs px-3 py-1 rounded-full border border-blue-800/50">
              {marketingImages.length} recursos disponibles
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {marketingImages.map((item) => (
              <div
                key={item.id}
                className="bg-gray-800/70 rounded-lg overflow-hidden border border-gray-700 hover:border-gray-600 transition-all shadow-md hover:shadow-lg group"
              >
                <div className="relative">
                  <img
                    src={item.placeholder}
                    alt={item.title}
                    className="w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent opacity-70"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white font-medium">
                          {item.title}
                        </div>
                        <div className="text-blue-300 text-sm">
                          {userData?.referralCode}
                        </div>
                      </div>
                      <button
                        onClick={() =>
                          downloadImage(item.placeholder, item.filename)
                        }
                        className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full transform group-hover:scale-110 transition-all"
                      >
                        <Download size={16} />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-gray-900/50 border-t border-gray-700">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-xs">
                      {item.type === "banner" &&
                        "Formato horizontal • Para web"}
                      {item.type === "story" &&
                        "Formato vertical • Para stories"}
                      {item.type === "post" && "Formato cuadrado • Para feed"}
                      {item.type === "banner-small" &&
                        "Formato pequeño • Para anuncios"}
                    </span>
                    <span className="text-gray-400 text-xs">
                      {item.type === "story" ? "1080x1920" : "1200x628"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contenido para Compartir */}
      {activeTab === "compartir" && (
        <div className="animate-fadeIn">
          <p className="text-gray-300 mb-5">
            Comparte tu código de referido directamente en tus redes sociales:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <button
              onClick={() => shareOnSocial("whatsapp")}
              className="flex items-center justify-center px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all hover:shadow-lg hover:shadow-green-900/20"
            >
              <span className="font-medium">Compartir en WhatsApp</span>
            </button>

            <button
              onClick={() => shareOnSocial("facebook")}
              className="flex items-center justify-center px-4 py-3 bg-blue-700 hover:bg-blue-800 text-white rounded-lg transition-all hover:shadow-lg hover:shadow-blue-900/20"
            >
              <span className="font-medium">Compartir en Facebook</span>
            </button>

            <button
              onClick={() => shareOnSocial("twitter")}
              className="flex items-center justify-center px-4 py-3 bg-blue-400 hover:bg-blue-500 text-white rounded-lg transition-all hover:shadow-lg hover:shadow-blue-900/20"
            >
              <span className="font-medium">Compartir en Twitter</span>
            </button>
          </div>

          <div className="bg-gray-800/70 p-5 rounded-lg border border-gray-700 hover:border-gray-600 transition-all shadow-md">
            <h4 className="text-white font-medium mb-3 flex items-center">
              <Share2 size={16} className="text-blue-400 mr-2" />
              Texto sugerido para compartir:
            </h4>
            <div className="bg-gray-900 p-4 rounded-md text-gray-300 text-sm border border-gray-700">
              <p>
                ¡Hola! Te invito a unirte a esta increíble plataforma de
                predicciones deportivas. Regístrate con mi código de referido:{" "}
                <span className="text-blue-400 font-bold">
                  {userData?.referralCode}
                </span>{" "}
                y obtén bonificaciones exclusivas.
              </p>
              <p className="mt-2 text-blue-400 break-all">{referralLink}</p>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={() =>
                  copyToClipboard(
                    `¡Hola! Te invito a unirte a esta increíble plataforma de predicciones deportivas. Regístrate con mi código de referido: ${userData?.referralCode} y obtén bonificaciones exclusivas.\n\n${referralLink}`,
                    "share-text"
                  )
                }
                className={`flex items-center text-sm px-3 py-1.5 rounded transition-all ${
                  activeCopyId === "share-text"
                    ? "bg-green-600 text-white"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                {activeCopyId === "share-text" ? (
                  <Check size={16} className="mr-1" />
                ) : (
                  <Copy size={16} className="mr-1" />
                )}
                {activeCopyId === "share-text" ? "¡Copiado!" : "Copiar texto"}
              </button>
            </div>
          </div>

          <div className="mt-6 bg-gray-900/40 p-4 rounded-lg border border-gray-800">
            <div className="flex items-center">
              <div className="bg-blue-500/20 p-2 rounded-md mr-3">
                <Check size={18} className="text-blue-400" />
              </div>
              <div>
                <h4 className="text-blue-300 text-sm font-medium">
                  Estadísticas de referidos
                </h4>
                <p className="text-gray-400 text-xs mt-1">
                  Has referido a 0 personas hasta ahora. ¡Comparte tu código
                  para aumentar tus beneficios!
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default MarketingTools;
